import type { CatalogProduct, Concentration, FragranceFamily, Gender, ProductBenefit } from "./products";
import { fallbackProductImage } from "./fallback-images";
import { normalizeSalesChannels } from "./sales-channels";

export type DatabaseProductRow = {
  id: string; slug: string; name: string; family: string | null; gender: string | null;
  concentration: string | null; notes: string | null; key_notes: string[] | null;
  price_cents: number;
  price_50ml_cents?: number | null;
  price_100ml_cents?: number | null;
  price_10ml_cents?: number | null;
  price_20ml_cents?: number | null;
  cover_image: string | null; badge: string | null; category_id: string;
  attributes: Record<string, unknown> | null; released_at?: string | null; created_at?: string;
  is_active?: boolean;
  sales_channels?: string[] | null;
  discount_percent?: number | null;
};

/** Reads editable PDP benefits from attributes; undefined means "use defaults". */
function mapBenefits(value: unknown): ProductBenefit[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: ProductBenefit[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") return undefined;
    const { text, enabled } = item as Record<string, unknown>;
    if (typeof text !== "string" || typeof enabled !== "boolean") return undefined;
    out.push({ text, enabled });
  }
  return out.length > 0 ? out : undefined;
}

export function mapDatabaseProduct(row: DatabaseProductRow): CatalogProduct {
  const attributes = row.attributes ?? {};
  const resolvedImage = row.cover_image || fallbackProductImage(row.slug, row.category_id);
  console.info("[product-image-resolver]", {
    productId: row.id,
    slug: row.slug,
    databaseCoverImage: row.cover_image,
    resolvedImage,
    source: row.cover_image ? "database" : "demo-fallback",
  });
  const p50 = (row.price_50ml_cents ?? row.price_cents) / 100;
  const p100 = (row.price_100ml_cents ?? row.price_cents) / 100;
  const p10 = (row.price_10ml_cents ?? 0) / 100;
  const p20 = (row.price_20ml_cents ?? 0) / 100;
  return {
    id: row.id, slug: row.slug, name: row.name,
    family: (row.family as FragranceFamily | null) ?? "Floral",
    gender: (row.gender as Gender | null) ?? "Unisex",
    concentration: (row.concentration as Concentration | null) ?? "EDP",
    notes: row.notes ?? "", keyNotes: row.key_notes ?? [],
    // Catalog display price = "starting from" = the 50 ML price.
    price: p50,
    prices: { "10": p10, "20": p20, "50": p50, "100": p100 },
    // Database first; bundled demo image only when no cover image exists yet.
    img: resolvedImage,
    badge: (row.badge as CatalogProduct["badge"]) ?? undefined,
    collectionId: (attributes.collectionId as CatalogProduct["collectionId"]) ?? "signatures",
    createdAt: row.released_at?.slice(0, 10) ?? row.created_at?.slice(0, 10) ?? "",
    categoryId: row.category_id,
    sku: typeof attributes.sku === "string" ? attributes.sku : undefined,
    shortDescription: typeof attributes.shortDescription === "string" ? attributes.shortDescription : undefined,
    longDescription: typeof attributes.longDescription === "string" ? attributes.longDescription : undefined,
    gallery: Array.isArray(attributes.gallery) ? attributes.gallery.filter((v): v is string => typeof v === "string") : [],
    status: (attributes.lifecycleStatus as CatalogProduct["status"]) ?? (row.is_active === false ? "archived" : "active"),
    showInBestSellers: attributes.showInBestSellers === true,
    showInNewArrivals: attributes.showInNewArrivals === true,
    salesChannels: normalizeSalesChannels(row.sales_channels),
    benefits: mapBenefits(attributes.benefits),
    attributes: {
      ...attributes,
      discountPercent: Number(row.discount_percent ?? 0),
    },
    discountPercent: Number(row.discount_percent ?? 0),
  };
}